import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Drawer from "@intility/bifrost-react/Drawer";
import Message from "@intility/bifrost-react/Message";
import Table from "@intility/bifrost-react/Table";
import { useState } from "react";
import { useOnboardedCompanies } from "~/api/companies";
import type { OnboardedCompany } from "~/api/types";
import { PageLayout } from "../../components/layout/PageLayout";
import { CompanyDetails } from "./CompanyDetails";
import { OnboardForm, ReadinessNotice } from "./OnboardForm";
import { rowState } from "./onboarding";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Companies" }];

// The form is open for a new company ("new") or for one row of the record.
type FormTarget = "new" | OnboardedCompany | null;

// Customer onboarding (ADR-0058). The rows come from the onboarded-company record in
// keycloak-api, and every action on the page is the same onboard call.
export default function CompaniesPage() {
  const { data: companies, isLoading, error } = useOnboardedCompanies();
  const [formTarget, setFormTarget] = useState<FormTarget>(null);

  // Looked up in the fresh list, so a mode switch shows in the open drawer.
  const [manageId, setManageId] = useState<string | null>(null);
  const manageCompany = companies?.find((c) => c.companyId === manageId);

  const formCompany =
    formTarget && formTarget !== "new" ? formTarget : undefined;

  return (
    <PageLayout
      breadcrumbs={BREADCRUMBS}
      title="Companies"
      description="The customer companies that Intility ID serves. Onboarding links the company in the access store, creates its Keycloak Organization, links the Microsoft provider and routes its email domains. The user sync then pulls the users of its Entra tenants."
      actions={
        <Button variant="basic" onClick={() => setFormTarget("new")}>
          Onboard company
        </Button>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <ReadinessNotice />

        {isLoading ? (
          <p className="bf-p">Loading…</p>
        ) : error ? (
          <Message state="alert" header="Error">
            {error instanceof Error ? error.message : String(error)}
          </Message>
        ) : !companies || companies.length === 0 ? (
          <p className="bf-p" style={{ color: "var(--bfc-base-c-2)" }}>
            No companies are onboarded yet.
          </p>
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Company</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Domains</Table.HeaderCell>
                <Table.HeaderCell>Tenants</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {companies.map((c) => (
                <CompanyRow
                  key={c.companyId}
                  company={c}
                  onOpen={() => setManageId(c.companyId)}
                />
              ))}
            </Table.Body>
          </Table>
        )}
      </div>

      <Drawer
        header={manageCompany?.displayName ?? "Company"}
        isOpen={manageCompany !== undefined}
        onRequestClose={() => setManageId(null)}
      >
        {manageCompany && (
          <CompanyDetails
            company={manageCompany}
            onEdit={() => {
              setManageId(null);
              setFormTarget(manageCompany);
            }}
          />
        )}
      </Drawer>

      <Drawer
        header={
          formCompany
            ? `${rowState(formCompany).action}: ${formCompany.displayName ?? formCompany.companyId}`
            : "Onboard company"
        }
        isOpen={formTarget !== null}
        onRequestClose={() => setFormTarget(null)}
      >
        {formTarget !== null && (
          <OnboardForm
            // A new key resets the form state when another row opens it.
            key={formCompany?.companyId ?? "new"}
            company={formCompany}
            onDone={() => setFormTarget(null)}
          />
        )}
      </Drawer>
    </PageLayout>
  );
}

function CompanyRow({
  company,
  onOpen,
}: {
  company: OnboardedCompany;
  onOpen: () => void;
}) {
  const state = rowState(company);
  return (
    <Table.Row
      onClick={onOpen}
      onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && onOpen()}
      tabIndex={0}
      style={{ cursor: "pointer" }}
    >
      <Table.Cell>{company.displayName ?? "(no display name)"}</Table.Cell>
      <Table.Cell>
        <Badge pill state={state.badge}>
          {state.label}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        {company.domains.length === 0 ? (
          "No domain"
        ) : (
          <div className="bf-small">
            {company.domains.map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
        )}
      </Table.Cell>
      <Table.Cell>
        {company.tenants.length === 0 ? (
          "No tenant"
        ) : (
          <div className="bf-small">
            {company.tenants.map((t) => (
              <div key={t.tenantId}>
                <code>{t.tenantId}</code>
              </div>
            ))}
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  );
}
